// CLI — `grid wire folder <entity>` (and its top-level alias `grid link
// <entity>`).
//
// Binds the current working directory to an existing grid entity by writing
// `.cloudgrid/link.json` — WITHOUT downloading source. The lighter sibling of
// `pickup` / `download`, which fetch the code AND bind. Per Decision 042 Mode 1
// (§wire / §link), `wire folder` is the unifier subcommand and `link` is the
// kept common-case alias; both run this one core, so they are functionally
// identical.
//
// Server contract: GET /api/v2/runtimes/:idOrName — the detail response is the
// source of truth for the canonical name, kind, and grid that link.json needs.
// Entity resolution mirrors `clone`/`pickup`: a UUID or `org/slug` ref is
// globally addressable; a bare slug is resolved across every grid you belong
// to; `--org` pins resolution to one grid.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { getActiveOrg, type ActiveOrg } from '../active-org.js';
import { isGlobalEntityRef } from '../services/entity-ref.js';
import {
  resolveBareSlugAcrossOrgs,
  bareSlugNotFoundMessage,
} from '../services/resolve-bare-slug.js';
import { appendGitignoreLine, writeLink, type LinkArtifact } from '../services/link.js';
import { getCliVersion } from '../cli-version.js';
import { CloudGridError } from '../utils.js';
import { addGridOption, gridSlug, type GridOpts } from '../grid-opt.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';

/** Runtime-detail shape read from `GET /api/v2/runtimes/:idOrName`. Only the
 *  fields the link needs. Mirrors `clone.ts`'s `RuntimeDetail`. */
interface RuntimeDetail {
  entity_id?: string;
  name?: string;
  kind?: 'app' | 'agent';
  url?: string;
  org?: { org_id?: string; slug?: string };
}

export interface WireFolderOpts extends GridOpts {
  /**
   * `--grid <slug>` (primary) / `--org <slug>` (deprecated alias) — one-shot
   * grid override (same semantics as plug/clone). Read via {@link gridSlug}.
   */
  json?: boolean;
  output?: string;
}

export interface WireFolderEnv {
  cwd: string;
}

/**
 * Testable core for `wire folder` / `link`: resolve an existing entity and
 * write `.cloudgrid/link.json` pointing the cwd at it. No code download.
 *
 * Throws `CloudGridError` on every failure path; the command action catches
 * and routes through `handleV2Error`. Idempotent w.r.t. re-binding — `link` is
 * explicitly the recovery / re-binding verb, so an existing link is overwritten
 * (the write is atomic; see `services/link.ts`).
 */
export async function runWireFolder(
  target: string | undefined,
  opts: WireFolderOpts,
  client: IV2Client,
  env: WireFolderEnv,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const cwd = env.cwd;
  let idOrName = (target ?? '').trim();
  if (idOrName.length === 0) {
    throw new CloudGridError(
      'Provide an entity to bind to. Usage: grid wire folder <entity> (alias: grid link <entity>)',
    );
  }

  // Entity resolution (mirrors clone.ts):
  //  - `--org` override → resolve in that grid.
  //  - UUID or `org/slug` ref → globally addressable, no active-grid round-trip.
  //  - bare slug, no `--org` → search every grid you belong to and resolve
  //    uniquely instead of silently scoping to the active grid.
  let activeOrg: ActiveOrg | null = null;
  let scoped = client;
  const grid = gridSlug(opts);
  if (grid !== undefined) {
    activeOrg = await getActiveOrg(client, { override: grid });
    scoped = client.withOrgSlug(activeOrg.slug);
  } else if (!isGlobalEntityRef(idOrName)) {
    const match = await resolveBareSlugAcrossOrgs(idOrName, client);
    if (!match) {
      throw new CloudGridError(bareSlugNotFoundMessage(idOrName, 'wire folder'));
    }
    // Address the resolved entity by its UUID — globally addressable.
    idOrName = match.entity_id;
  }

  // Resolve the entity. A 404 / 403 propagates with the server's wording.
  const runtime = await scoped.get<RuntimeDetail>(
    `/api/v2/runtimes/${encodeURIComponent(idOrName)}`,
  );
  const entityId = runtime?.entity_id;
  if (!entityId) {
    throw new CloudGridError(
      `Server returned no entity_id for ${idOrName}. Report this as a bug.`,
    );
  }
  const name = runtime?.name ?? idOrName;

  // Decision 012 §4 names `kind` as required on link.json. A missing or
  // unrecognised value is a server bug, not something to paper over.
  const kind = runtime?.kind;
  if (kind !== 'app' && kind !== 'agent') {
    throw new CloudGridError(
      `Server returned entity ${idOrName} without a valid 'kind' field. ` +
        `Expected 'app' or 'agent'. Report this as a bug.`,
    );
  }

  // The detail response's `org` block is the source of truth; the resolved
  // active grid (when present) is only a fallback for the `--org` path.
  const orgId = runtime?.org?.org_id ?? activeOrg?.org_id;
  const orgSlug = runtime?.org?.slug ?? activeOrg?.slug;
  if (!orgId || !orgSlug) {
    throw new CloudGridError(
      `Server returned no grid for ${idOrName}. Report this as a bug.`,
    );
  }

  const link: LinkArtifact = {
    entity_id: entityId,
    org_id: orgId,
    org_slug: orgSlug,
    kind,
    ...(runtime?.name ? { entity_slug: runtime.name } : {}),
    created_at: new Date().toISOString(),
    cli_version: getCliVersion(),
  };
  writeLink(link, cwd);
  appendGitignoreLine('.cloudgrid/', cwd);

  out.json(() => ({
    entity_id: entityId,
    name,
    kind,
    grid: orgSlug,
    url: runtime?.url ?? null,
  }));
  out.human(() => {
    const lines = [`Bound this folder to ${name} in grid ${orgSlug}.`];
    if (runtime?.url) lines.push(`Public URL: ${runtime.url}`);
    lines.push('Next: grid plug');
    return lines.join('\n');
  });
}

/**
 * Shared Commander action for both `wire folder` and the top-level `link`
 * alias. Exported so both registrations route through one symbol — a single
 * code path, not two copies that can drift.
 */
export async function wireFolderAction(
  entity: string | undefined,
  opts: WireFolderOpts,
): Promise<void> {
  try {
    const client = await getV2Client(gridSlug(opts));
    await runWireFolder(entity, opts, client, { cwd: process.cwd() });
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
}

/**
 * Build a `wire folder`-shaped command. `name` lets the same definition mount
 * as the `folder` subcommand of `wire` and as the top-level `link` alias.
 */
export function makeWireFolderCommand(name: string): Command {
  const cmd = new Command(name)
    .description('Bind this folder to an existing grid entity (no code download)')
    .argument('[entity]', 'Entity slug or id to bind this folder to')
    .option('--json', 'Machine-readable JSON output (alias of -o json)');
  addGridOption(cmd, 'Grid to resolve the entity in (defaults to the active grid)');
  addOutputOption(cmd);
  return cmd.action(wireFolderAction);
}
